import React from "react";
import { motion } from "framer-motion";
import { Baby, Heart, Sparkles, MessageCircle, Users, TrendingUp, Calendar, BookOpen, Shield, Bell, LineChart, Activity } from "lucide-react";
import FeatureDeepDive from "../components/FeatureDeepDive";

/* ---------- Accent widgets (feature-specific 3D-ish overlays around the phone) ---------- */

// Pregnancy — timeline dots orbiting the phone
export function PregnancyAccent({ reduced }) {
    return (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
                <motion.div
                    key={i}
                    className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-lg bg-white/70 border border-white/80 shadow-md text-[11px] font-bold text-femcare-ink"
                    style={{ top: `${8 + i * 18}%` }}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
                    animate={reduced ? { opacity: 1 } : { opacity: 1, x: i % 2 === 0 ? -120 - i * 8 : 120 + i * 8, y: [0, -4, 0] }}
                    transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                >
                    Week {8 + i * 6}
                </motion.div>
            ))}
        </div>
    );
}

// AI Coach — floating chat bubbles
export function AICoachAccent({ reduced }) {
    const msgs = [
        { text: "Is my cycle normal?", side: "right", top: "10%" },
        { text: "Try 15 min of walking today", side: "left", top: "28%" },
        { text: "Track your mood?", side: "right", top: "55%" },
        { text: "Rest well tonight", side: "left", top: "72%" },
    ];
    return (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
            {msgs.map((m, i) => (
                <motion.div
                    key={i}
                    className={`absolute max-w-[150px] rounded-2xl px-3 py-2 backdrop-blur-xl border shadow-lg text-[11px] leading-tight ${
                        m.side === "right"
                            ? "right-0 bg-femcare-pinkDeep/90 border-white/30 text-white"
                            : "left-0 bg-white/85 border-white/70 text-femcare-ink"
                    }`}
                    style={{ top: m.top }}
                    initial={{ opacity: 0, x: m.side === "right" ? 20 : -20 }}
                    animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0, y: [0, -6, 0] }}
                    transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                >
                    {m.text}
                </motion.div>
            ))}
        </div>
    );
}

// Daughter Care — twin cards (parent + daughter)
export function DaughterAccent({ reduced }) {
    return (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
            {[
                { label: "You", role: "Parent", side: "left", top: "18%", color: "#DB2777" },
                { label: "Aarohi", role: "Daughter · 12 yrs", side: "right", top: "62%", color: "#F472B6" },
            ].map((p, i) => (
                <motion.div
                    key={i}
                    className={`absolute rounded-2xl px-4 py-3 backdrop-blur-xl bg-white/85 border border-white/70 shadow-lg ${p.side === "left" ? "left-0" : "right-0"}`}
                    style={{ top: p.top, minWidth: 140 }}
                    initial={{ opacity: 0, x: p.side === "left" ? -20 : 20 }}
                    animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0, y: [0, -6, 0] }}
                    transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                >
                    <span className="block w-8 h-8 rounded-full mb-2" style={{ background: `linear-gradient(135deg,${p.color},${p.color}88)` }} />
                    <p className="text-xs font-bold text-femcare-ink">{p.label}</p>
                    <p className="text-[10px] text-femcare-inkSoft">{p.role}</p>
                </motion.div>
            ))}
        </div>
    );
}

// Community — floating topic pills at different depths
export function CommunityAccent({ reduced }) {
    const topics = ["PCOS", "TTC · Month 5", "First trimester", "PMS mood swings", "Puberty tips", "Post-partum"];
    return (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
            {topics.map((t, i) => {
                const positions = [
                    { top: "8%",  left: "-8%" },
                    { top: "22%", right: "-6%" },
                    { top: "45%", left: "-14%" },
                    { top: "58%", right: "-10%" },
                    { top: "75%", left: "-4%" },
                    { top: "88%", right: "-2%" },
                ];
                const p = positions[i];
                return (
                    <motion.span
                        key={i}
                        className="absolute inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 backdrop-blur-lg bg-white/80 border border-white/70 shadow-md text-[11px] font-bold text-femcare-ink"
                        style={{ ...p, opacity: 1 - Math.abs(i - 2.5) * 0.08 }}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={reduced ? { opacity: 0.9 } : { opacity: 0.95, scale: 1, y: [0, -8, 0] }}
                        transition={{ duration: 4 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-femcare-pinkDeep" />
                        {t}
                    </motion.span>
                );
            })}
        </div>
    );
}

// Insights — data cards emerging with sparkline/metric
export function InsightsAccent({ reduced }) {
    const cards = [
        { label: "Cycle length", value: "28d", trend: "+1", side: "left",  top: "10%" },
        { label: "Regularity",   value: "94%", trend: "↑",  side: "right", top: "26%" },
        { label: "Sleep avg",    value: "7h 12m", trend: "↑", side: "left", top: "58%" },
        { label: "Mood",         value: "Stable", trend: "↔", side: "right", top: "75%" },
    ];
    return (
        <div aria-hidden className="absolute inset-0 pointer-events-none">
            {cards.map((c, i) => (
                <motion.div
                    key={i}
                    className={`absolute rounded-2xl px-4 py-3 backdrop-blur-xl bg-white/85 border border-white/70 shadow-lg ${c.side === "left" ? "left-0" : "right-0"}`}
                    style={{ top: c.top, minWidth: 130 }}
                    initial={{ opacity: 0, x: c.side === "left" ? -20 : 20 }}
                    animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0, y: [0, -6, 0] }}
                    transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
                >
                    <p className="text-[9px] uppercase tracking-widest text-femcare-inkSoft font-bold">{c.label}</p>
                    <p className="text-lg font-extrabold text-femcare-ink leading-none mt-1">{c.value}</p>
                    <p className="text-[10px] text-femcare-pinkDeep font-semibold mt-1">{c.trend}</p>
                </motion.div>
            ))}
        </div>
    );
}

/* ---------- Configs ---------- */

const basePearls = [
    { s: 80, t: "18%", l: "8%",  d: 0 },
    { s: 42, t: "22%", l: "62%", d: 1 },
    { s: 110, t: "60%", l: "85%", d: 2 },
    { s: 55, t: "78%", l: "12%", d: 0.6 },
];

export const pregnancyConfig = {
    slug: "pregnancy",
    eyebrow: "FemCare · Pregnancy",
    title: { line1: "Week by loving week.", line2: "Every kick, every milestone." },
    subtitle: "A gentle companion for your 40-week journey — milestones, kick counter, nutrition prompts and calm mental-wellness moments.",
    headlineGradient: "linear-gradient(135deg,#F472B6 0%,#B98CB6 60%,#a87aa6 100%)",
    bodyGradient: "#FFFFFF",
    pageBg: `radial-gradient(ellipse 55% 40% at 20% 10%, rgba(247,225,232,0.55), transparent 60%),
             radial-gradient(ellipse 45% 40% at 90% 80%, rgba(228,218,236,0.45), transparent 60%),
             #FFFFFF`,
    ctaGradient: "linear-gradient(135deg,#a87aa6 0%,#B98CB6 55%,#F472B6 100%)",
    screenSrc: "/hero-femcare-daughter-poster.jpg",
    pearls: basePearls,
    Accent: PregnancyAccent,
    chapters: [
        { eyebrow: "Trimester 01", title: "The quiet first weeks.",    body: "From the moment the test turns positive, FemCare guides you gently — weekly baby-size illustrations, morning-sickness relief tips and a mood journal that never judges.", Icon: Baby },
        { eyebrow: "Trimester 02", title: "Milestones, kicks, joy.",   body: "Log first kicks, count them safely, and follow layered milestone cards — anatomy scan, baby-name shortlist, gentle exercise prompts.", Icon: Heart },
        { eyebrow: "Trimester 03", title: "Getting ready together.",   body: "Hospital-bag checklists, contraction timer, birth-plan builder — all beautifully calm, all private.", Icon: Bell },
        { eyebrow: "The 4th trimester", title: "Support after delivery.", body: "Post-partum mood check-ins, feeding logs, healing timelines, and gentle affirmations for the parent AND the newborn.", Icon: Shield },
    ],
    cta: { script: "your pregnancy, softer", title1: "Every kick.", title2: "Every milestone." },
};

export const aiCoachConfig = {
    slug: "ai-coach",
    eyebrow: "FemCare · AI Health Coach",
    title: { line1: "Ask anything.", line2: "Get answers that get you." },
    subtitle: "A private, cycle-aware assistant that answers your questions in your language — educational, judgement-free, always on.",
    headlineGradient: "linear-gradient(135deg,#EC4899 0%,#8f638d 60%,#3B0764 100%)",
    bodyGradient: "#FFFFFF",
    pageBg: `radial-gradient(ellipse 55% 40% at 80% 15%, rgba(238,214,232,0.5), transparent 60%),
             radial-gradient(ellipse 45% 40% at 15% 85%, rgba(217,207,232,0.5), transparent 60%),
             #FFFFFF`,
    ctaGradient: "linear-gradient(135deg,#3B0764 0%,#8f638d 40%,#EC4899 100%)",
    screenSrc: "/femcare-ui-daughter.jpg",
    pearls: basePearls,
    Accent: AICoachAccent,
    chapters: [
        { eyebrow: "Chapter 01", title: "Trained on clinician-reviewed content.", body: "The AI Coach never invents answers — it references evidence-based women's-health content and always points to a professional when questions cross into medical advice.", Icon: Sparkles },
        { eyebrow: "Chapter 02", title: "Cycle-aware, life-stage aware.",         body: "Because it sees your logged patterns, its replies factor in where you are in your cycle, whether you're pregnant, or in perimenopause.", Icon: Activity },
        { eyebrow: "Chapter 03", title: "In English and Hindi.",                  body: "Ask in whichever language feels natural. Voice input is transcribed on-device where supported so raw audio never leaves your phone.", Icon: MessageCircle },
        { eyebrow: "Chapter 04", title: "Private. Never for training.",           body: "Your conversations are encrypted end-to-end and never used to train third-party models. You can delete any thread with a swipe.", Icon: Shield },
    ],
    cta: { script: "answers you can trust", title1: "Your body's questions.", title2: "Answered privately." },
};

export const daughterCareConfig = {
    slug: "daughter-care",
    eyebrow: "FemCare · Daughter Care",
    title: { line1: "Puberty education,", line2: "shared with love." },
    subtitle: "Age-appropriate guidance for her — with a parent view designed for connection, not surveillance.",
    headlineGradient: "linear-gradient(135deg,#F472B6 0%,#F9A8D4 60%,#FED7C3 100%)",
    bodyGradient: "#FFFFFF",
    pageBg: `radial-gradient(ellipse 55% 40% at 30% 10%, rgba(250,222,236,0.55), transparent 60%),
             radial-gradient(ellipse 45% 40% at 80% 80%, rgba(232,214,236,0.45), transparent 60%),
             #FFFFFF`,
    ctaGradient: "linear-gradient(135deg,#F472B6 0%,#EC4899 50%,#a87aa6 100%)",
    screenSrc: "/daughter-care-preview.webp",
    pearls: basePearls,
    Accent: DaughterAccent,
    chapters: [
        { eyebrow: "Chapter 01", title: "Two accounts, one bond.",     body: "You each have your own private space. She logs and learns in age-appropriate language; you see only the summary she chooses to share.", Icon: Heart },
        { eyebrow: "Chapter 02", title: "Puberty, gently explained.",  body: "Illustrated guides adapt to her age — from first-period readiness to body-image confidence — reviewed by paediatricians and mothers.", Icon: BookOpen },
        { eyebrow: "Chapter 03", title: "Reminders that feel like care.", body: "Pill, hydration, pad-restock — set together, sent discreetly. Lock-screen previews never spell out cycle info.", Icon: Bell },
        { eyebrow: "Chapter 04", title: "A safe space to ask questions.", body: "She can ask the AI Coach anything — privately. You get calm summaries, not a live feed.", Icon: Shield },
    ],
    cta: { script: "together, at every stage", title1: "Her journey.", title2: "Your quiet support." },
};

export const communityConfig = {
    slug: "community",
    eyebrow: "FemCare · Community",
    title: { line1: "You are not", line2: "alone in this." },
    subtitle: "Anonymous, moderated conversations with women in your exact season of life — no ads, no judgement, no data mining.",
    headlineGradient: "linear-gradient(135deg,#8F638D 0%,#DB2777 60%,#EC4899 100%)",
    bodyGradient: "#FFFFFF",
    pageBg: `radial-gradient(ellipse 55% 40% at 25% 15%, rgba(228,213,232,0.55), transparent 60%),
             radial-gradient(ellipse 45% 40% at 85% 75%, rgba(217,205,232,0.5), transparent 60%),
             #FFFFFF`,
    ctaGradient: "linear-gradient(135deg,#8F638D 0%,#DB2777 60%,#EC4899 100%)",
    screenSrc: "/hero-femcare-sisterhood-poster.jpg",
    pearls: basePearls,
    Accent: CommunityAccent,
    chapters: [
        { eyebrow: "Chapter 01", title: "Anonymous by default.",         body: "Post with a pseudonym. Your cycle, your feed, and your identity are never linked in any exportable form.", Icon: Users },
        { eyebrow: "Chapter 02", title: "Circles, not timelines.",       body: "Join spaces that mirror where you are — TTC, first-trimester, PCOS, perimenopause, or puberty parenting.", Icon: Heart },
        { eyebrow: "Chapter 03", title: "Moderated with warmth.",        body: "Trained moderators plus AI flagging keep the conversations kind. Anything unsafe gets removed within minutes.", Icon: Shield },
        { eyebrow: "Chapter 04", title: "Real women. Real seasons.",     body: "No influencers, no supplements, no clickbait. Just women navigating the same phase you are — held together softly.", Icon: MessageCircle },
    ],
    cta: { script: "you are held", title1: "A quiet space.", title2: "For every season of you." },
};

export const insightsConfig = {
    slug: "insights",
    eyebrow: "FemCare · Insights",
    title: { line1: "Patterns you'd", line2: "miss on your own." },
    subtitle: "Beautiful weekly and monthly reports that surface trends in mood, sleep, symptoms and energy — private, printable, yours.",
    headlineGradient: "linear-gradient(135deg,#DB2777 0%,#a87aa6 60%,#111827 100%)",
    bodyGradient: "#FFFFFF",
    pageBg: `radial-gradient(ellipse 55% 40% at 15% 10%, rgba(240,224,236,0.5), transparent 60%),
             radial-gradient(ellipse 45% 40% at 90% 80%, rgba(220,207,232,0.5), transparent 60%),
             #FFFFFF`,
    ctaGradient: "linear-gradient(135deg,#111827 0%,#a87aa6 45%,#DB2777 100%)",
    screenSrc: "/insights-articles.png",
    pearls: basePearls,
    Accent: InsightsAccent,
    chapters: [
        { eyebrow: "Chapter 01", title: "Weekly & monthly reports.",   body: "Elegant PDF summaries of cycle metrics, mood trends and symptom clusters — generated automatically as you log.", Icon: LineChart },
        { eyebrow: "Chapter 02", title: "Correlations you can see.",   body: "See how sleep affects cramps, how mood shifts through your luteal phase, and when energy peaks in your cycle.", Icon: TrendingUp },
        { eyebrow: "Chapter 03", title: "Wearable-friendly.",          body: "Sync with Apple Health, Fitbit or Oura and let FemCare weave your body data into the same private report.", Icon: Activity },
        { eyebrow: "Chapter 04", title: "Save it. Print it. Share it.", body: "Beautiful vector-quality exports you can print, save, or share with someone you trust — nothing is uploaded anywhere.", Icon: Calendar },
    ],
    cta: { script: "insight, made calm", title1: "Your patterns.", title2: "Beautifully surfaced." },
};

/* ---------- Route wrappers ---------- */

export const FeaturePregnancy    = () => <FeatureDeepDive config={pregnancyConfig} />;
export const FeatureAICoach      = () => <FeatureDeepDive config={aiCoachConfig} />;
export const FeatureDaughterCare = () => <FeatureDeepDive config={daughterCareConfig} />;
export const FeatureCommunity    = () => <FeatureDeepDive config={communityConfig} />;
export const FeatureInsights     = () => <FeatureDeepDive config={insightsConfig} />;
